import { useMemo } from 'react';

import Link from '@placekit/uikit/components/Link';
import Logo from '@placekit/uikit/components/Logo';
import SocialIcon from '@placekit/uikit/components/SocialIcon';

import Container from 'components/Container';
import { useT } from 'i18n';

const Footer = () => {
  const t = useT();

  // Links point at the FR twin when the page has one (`t.path`), and stay on the
  // EN page otherwise.
  const sections = useMemo(
    () => [
      {
        title: t('common.footer.product.title'),
        links: [
          { label: t('common.footer.product.overview'), href: t.path('/') },
          { label: t('common.footer.product.features'), href: t.path('/#features'), scroll: false },
          // EN-only product page for now, linked from both locales.
          { label: t('common.footer.product.autocomplete'), href: '/address-autocomplete-api' },
          { label: t('common.footer.product.pricing'), href: t.path('/pricing') },
        ],
      },
      {
        title: t('common.footer.developers.title'),
        links: [
          { label: t('common.footer.developers.resources'), href: '/developers' },
          {
            label: t('common.footer.developers.libs'),
            href: '/developers/#libs',
            scroll: false,
          },
          {
            label: t('common.footer.developers.reference'),
            href: '/developers/#reference',
            scroll: false,
          },
          {
            label: t('common.footer.developers.examples'),
            href: '/developers/#examples',
            scroll: false,
          },
          {
            label: t('common.footer.developers.status'),
            href: 'https://placekit.statuspage.io',
            target: '_blank',
          },
        ],
      },
      {
        title: t('common.footer.terms.title'),
        links: [
          { label: t('common.footer.terms.cgv'), href: '/terms' },
          { label: t('common.footer.terms.tos'), href: '/terms/en' },
          { label: t('common.footer.terms.privacy'), href: '/terms/privacy' },
          { label: t('common.footer.terms.fup'), href: '/terms/fup' },
          { label: t('common.footer.terms.cookies'), href: '/terms/cookies' },
          { label: t('common.footer.terms.coverage'), href: '/terms/coverage' },
        ],
      },
      {
        title: t('common.footer.getStarted.title'),
        links: [
          { label: t('common.footer.getStarted.algolia'), href: '/lp/algolia-places' },
          { label: t('common.footer.getStarted.google'), href: '/lp/google-places' },
          {
            label: t('common.footer.getStarted.register'),
            href: 'https://app.placekit.io/auth/register',
          },
          {
            label: t('common.footer.getStarted.signIn'),
            href: 'https://app.placekit.io/auth/signin',
          },
        ],
      },
      {
        title: t('common.footer.resources.title'),
        links: [
          { label: t('common.footer.resources.blog'), href: '/blog' },
          { label: t('common.footer.resources.about'), href: '/about' },
          { label: t('common.footer.resources.contact'), href: '/about#contact', scroll: false },
        ],
      },
    ],
    [t],
  );

  return (
    <footer className="pt-16 pb-8 border-t border-gray-700 bg-gray-900">
      <Container className="gap-8 columns-1 sm:columns-2 md:columns-4">
        <div className="pb-8 space-y-6 text-gray-400 break-inside-avoid md:break-after-column">
          {/* block wrapper: the inline logo svg keeps its line box under Tailwind 4 `space-y-*` */}
          <div>
            <Logo size="medium" full={true} />
          </div>
          <div className="flex items-center gap-4">
            {[
              { name: 'ProductHunt', url: 'https://www.producthunt.com/products/placekit' },
              { name: 'Twitter', url: 'https://twitter.com/placekitapi' },
              { name: 'LinkedIn', url: 'https://linkedin.com/company/placekit' },
              { name: 'GitHub', url: 'https://github.com/placekit' },
            ].map(({ name, url }) => (
              <a
                key={name}
                href={url}
                target="_blank"
                title={name}
                className="text-gray-200 hover:text-gray-50 focus:text-gray-50"
                rel="noreferrer"
              >
                <SocialIcon name={name.toLocaleLowerCase()} branded={false} size="medium" />
              </a>
            ))}
          </div>
          <p className="text-sm">
            {t('common.footer.rights')}
            <br />
            {t('common.footer.madeIn')}
          </p>
        </div>
        {sections.map(({ title, links }) => (
          <nav key={title} className="pb-8 space-y-2 break-inside-avoid">
            <p className="font-semibold text-gray-400">{title}</p>
            <ul className="space-y-2">
              {links.map(({ label, ...linkProps }) => (
                <li key={label}>
                  <Link
                    {...linkProps}
                    className="text-gray-300 hover:text-gray-50 focus:text-gray-50 hover:underline focus:underline underline-offset-2 decoration-2 decoration-gray-600"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        ))}
      </Container>
    </footer>
  );
};

export default Footer;
